/**
 * Universal drop layer — the app-wide "drop it, I'll sort it" surface
 * (COMMAND-CENTER-REDESIGN.md decision 5, owner-mandated app law).
 *
 * Mounted once in AppShell so it lives on EVERY page. Drop a file anywhere —
 * or tap the always-visible drop pill to pick one (tablet/keyboard path, since
 * native drag-and-drop is mouse-only) — and it is routed by what it IS, not
 * where it was dropped:
 *
 *   PDF   → work-order intake, which the server classifies:
 *             · test report → filed against its tested job, or held for one
 *               answer on the City Filing Queue when it can't be matched
 *             · work order  → intake item; when you're on a job/location page
 *               its address is checked against the page and it auto-links on a
 *               match — never a silent link under the wrong customer
 *             · unrecognized PDF + you're on a job/location page → it just
 *               lands there as a document ("if it doesn't know what it is and
 *               you're on a job/location page, it lands there")
 *             · unrecognized PDF with no page context → stays in intake for
 *               review (the ambiguous one-answer case)
 *   image → job/location photo (with a category chip) when on a job/location page
 *   other → that page's Paperwork/documents when on a job/location page
 *
 * Every auto-route drops a toast; where the action is cleanly reversible (a job
 * attachment, an intake item) the toast carries Undo. The safety net for the
 * rest — "find it again if it went to the wrong place" — is the per-user
 * My Uploads page. Office/Admin only; techs use the field flag flow.
 *
 * This supersedes the per-page PageDropZone (removed from the job & site pages
 * once this landed) — the drop pill is now the only permanent drop chrome.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { capturePageContext } from "@/lib/page-context";
import { captureClientError } from "@/lib/sentry";
import { isImageFile, isPdfFile, normalizePdfFiles, useWindowFileDrag } from "@/lib/file-intake";
import { useLocalDropOwnerActive } from "@/lib/drop-capture";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
    AlertTriangle,
    CheckCircle2,
    FileText,
    Image as ImageIcon,
    Link2,
    Loader2,
    UploadCloud,
    Undo2,
    X,
    XCircle,
} from "lucide-react";

/** Where the current page says a drop should land. `none` = off-context. */
type DropTarget =
    | { kind: "job"; jobId: string; label: string }
    | { kind: "site"; siteId: string; label: string }
    | { kind: "none"; label: string };

const PHOTO_CHIPS = ["device", "area", "tag", "doc"] as const;
type PhotoChip = (typeof PHOTO_CHIPS)[number];

type EntryState =
    | "uploading"
    | "extracting"
    | "checking"
    | "filed" // terminal success
    | "mismatch" // WO address didn't match — awaiting office decision
    | "confirm" // test report serial ≠ this job's device — awaiting confirm (Issue #289)
    | "pick" // test report on a multi-device job — office picks which device
    | "undone"
    | "error";

/** A device on the job the office dropped a report on (confirm/pick cards). */
interface JobDevice {
    assetId: string;
    serial: string | null;
    label: string;
}

interface DropEntry {
    key: string;
    file: File;
    name: string;
    target: DropTarget;
    route: "photo" | "document" | "work_order";
    state: EntryState;
    message: string;
    thumbUrl: string | null;
    chip: PhotoChip;
    /** job_attachments id (photo/doc on a job) — enables re-tag + undo. */
    attachmentId: string | null;
    /** work_order_intake item id. */
    itemId: string | null;
    /** "Open where it landed" link. */
    landedHref: string | null;
    landedLabel: string | null;
    /** Reversal for the toast/tray Undo — only set when cleanly reversible. */
    undo: (() => Promise<void>) | null;
    woAddress?: string;
    pageAddress?: string;
    /** Confirm rip cord (Issue #289): report SN didn't match the job's device. */
    confirmData?: { assetId: string; deviceLabel: string; deviceSerial: string | null; reportSerial: string | null };
    /** Pick card: the job has several devices — which one is this report for? */
    pickDevices?: JobDevice[];
    busy?: boolean;
}

const POLL_MS = 2_500;
const POLL_LIMIT_MS = 3 * 60_000;
const UNDO_MS = 8_000;

function sleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
}

/** Resolve the current page into a drop target (route + reported snapshot). */
function resolveTarget(): DropTarget {
    const ctx = capturePageContext();
    const ent = ctx.entity;
    if (ent?.type === "jobs") {
        return { kind: "job", jobId: ent.id, label: ctx.snapshot?.label ?? "this job" };
    }
    if (ent?.type === "sites") {
        return { kind: "site", siteId: ent.id, label: ctx.snapshot?.label ?? "this location" };
    }
    return { kind: "none", label: ctx.page };
}

export function UniversalDropLayer() {
    const qc = useQueryClient();
    const location = useLocation();
    const dragActive = useWindowFileDrag();
    // A modal/dialog that owns its own file drops (the email composer, …) can
    // register itself so this app-wide overlay steps aside — otherwise its
    // z-[70] surface would swallow a file dragged onto that modal.
    const localDropOwner = useLocalDropOwnerActive();
    const [overlayHover, setOverlayHover] = useState(false);
    const [entries, setEntries] = useState<DropEntry[]>([]);
    const keyCounter = useRef(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Preview target for the overlay label — recomputed as the route changes
    // (location dep) and while a drag is in progress.
    const previewTarget = useMemo(() => resolveTarget(), [location.pathname, location.search, dragActive]);

    // The closeout job workspace owns its own drop handling per station: the
    // Documents station files reports against the open job, and Send-off's
    // "Additional files" uploader attaches to the packet. Our full-screen drag
    // overlay (fixed inset-0, z-70) would sit on top of those and swallow the
    // drop, so a file could never reach the intended zone — you literally
    // couldn't drag into the drop zone. Owner call: suppress the overlay while a
    // closeout job is open so those scoped zones receive the drop. The bare
    // closeout queue (no ?job) keeps the universal overlay. The window-level
    // dragover preventDefault (useWindowFileDrag) still runs, so a missed drop
    // won't navigate the browser to the file.
    //
    // Same reasoning for a registered local drop owner (the email composer): it
    // handles its own attachment drops, so this overlay steps aside while it's
    // open — a file dragged into the composer lands on the email, not here.
    const suppressOverlay =
        (location.pathname === "/close-out" && new URLSearchParams(location.search).has("job")) || localDropOwner;

    const patch = useCallback((key: string, p: Partial<DropEntry>) => {
        setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...p } : e)));
    }, []);

    const invalidate = useCallback(
        (target: DropTarget) => {
            void qc.invalidateQueries({ queryKey: ["work-order-intake"] });
            void qc.invalidateQueries({ queryKey: ["attachments"] });
            void qc.invalidateQueries({ queryKey: ["my-uploads"] });
            if (target.kind === "job") {
                void qc.invalidateQueries({ queryKey: ["job", target.jobId] });
                void qc.invalidateQueries({ queryKey: ["job", target.jobId, "documents"] });
                void qc.invalidateQueries({ queryKey: ["job", target.jobId, "work-orders-filled"] });
                void qc.invalidateQueries({ queryKey: ["activity-timeline"] });
            }
            if (target.kind === "site") {
                void qc.invalidateQueries({ queryKey: ["site-documents", target.siteId] });
                void qc.invalidateQueries({ queryKey: ["site-summary", target.siteId] });
            }
        },
        [qc],
    );

    // ── Terminal helpers ──────────────────────────────────────────────────

    /** Mark an entry filed, fire its toast (with Undo when reversible). */
    const finishFiled = useCallback(
        (
            entry: DropEntry,
            opts: {
                message: string;
                landedHref?: string | null;
                landedLabel?: string | null;
                undo?: (() => Promise<void>) | null;
            },
        ) => {
            patch(entry.key, {
                state: "filed",
                busy: false,
                message: opts.message,
                landedHref: opts.landedHref ?? null,
                landedLabel: opts.landedLabel ?? null,
                undo: opts.undo ?? null,
            });
            invalidate(entry.target);
            if (opts.undo) {
                toast.success(opts.message, {
                    duration: UNDO_MS,
                    action: { label: "Undo", onClick: () => void runUndo({ ...entry, undo: opts.undo ?? null }) },
                });
            } else if (opts.landedHref) {
                toast.success(opts.message);
            } else {
                toast.success(opts.message);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [patch, invalidate],
    );

    async function runUndo(entry: DropEntry): Promise<void> {
        if (!entry.undo) return;
        patch(entry.key, { busy: true });
        try {
            await entry.undo();
            patch(entry.key, {
                state: "undone",
                busy: false,
                undo: null,
                message: `Undone — ${entry.name} was not filed`,
                landedHref: null,
            });
            invalidate(entry.target);
            toast.success("Undone");
        } catch (e) {
            patch(entry.key, { busy: false });
            toast.error("Couldn't undo that");
            captureClientError(e, { source: "UniversalDropLayer.runUndo", fileName: entry.name });
        }
    }

    // ── Route: photo / document (job or site) ─────────────────────────────

    async function uploadPhotoOrDoc(entry: DropEntry): Promise<void> {
        const target = entry.target;
        const fd = new FormData();
        fd.append("file", entry.file);
        try {
            if (target.kind === "job") {
                fd.append("jobId", target.jobId);
                fd.append("category", entry.route === "photo" ? "photo" : "document");
                if (entry.route === "photo") fd.append("photoTag", entry.chip);
                const res = await api.postForm<{ attachment: { id: string } }>("/api/attachments/upload", fd);
                const id = res.attachment.id;
                finishFiled(entry, {
                    message:
                        entry.route === "photo"
                            ? `Filed as photo (${entry.chip}) on ${target.label}`
                            : `Filed to Paperwork on ${target.label}`,
                    landedHref: `/jobs/${target.jobId}`,
                    landedLabel: "Open job",
                    undo: async () => {
                        await api.delete(`/api/attachments/${id}`);
                    },
                });
                patch(entry.key, { attachmentId: id });
            } else if (target.kind === "site") {
                fd.append("category", entry.route === "photo" ? "photo" : "document");
                if (entry.route === "photo") fd.append("notes", `tag: ${entry.chip}`);
                await api.postForm(`/api/sites/${target.siteId}/attachments`, fd);
                // No site-attachment delete endpoint → no destructive undo;
                // My Uploads is the recovery path. Offer a "view" instead.
                finishFiled(entry, {
                    message:
                        entry.route === "photo"
                            ? `Filed as location photo on ${target.label}`
                            : `Filed to location documents on ${target.label}`,
                    landedHref: `/sites/${target.siteId}`,
                    landedLabel: "Open location",
                });
            }
        } catch (e) {
            patch(entry.key, { state: "error", message: "Upload failed" });
            captureClientError(e, { source: "UniversalDropLayer.uploadPhotoOrDoc", fileName: entry.name });
        }
    }

    // ── Route: work order (intake → poll → address check) ─────────────────

    async function pollItem(itemId: string): Promise<{ status: string } | null> {
        const start = Date.now();
        while (Date.now() - start < POLL_LIMIT_MS) {
            await sleep(POLL_MS);
            try {
                const res = await api.get<{ item: { status: string } }>(`/api/work-order-intake/items/${itemId}`);
                if (res.item.status !== "processing") return res.item;
            } catch {
                /* transient — keep polling until the cap */
            }
        }
        return null;
    }

    /** Auto-land a not-a-work-order PDF as a document on the current page. */
    async function autoLandUnknownPdf(entry: DropEntry, itemId: string | null): Promise<void> {
        const target = entry.target;
        if (target.kind === "none") return; // off-context handled by the caller
        const fd = new FormData();
        fd.append("file", entry.file);
        try {
            if (target.kind === "job") {
                fd.append("jobId", target.jobId);
                fd.append("category", "document");
                const res = await api.postForm<{ attachment: { id: string } }>("/api/attachments/upload", fd);
                const id = res.attachment.id;
                if (itemId)
                    await api
                        .patch(`/api/work-order-intake/items/${itemId}`, { status: "canceled" })
                        .catch(() => undefined);
                patch(entry.key, { attachmentId: id });
                finishFiled(entry, {
                    message: `Not a recognized work order — filed to Paperwork on ${target.label}`,
                    landedHref: `/jobs/${target.jobId}`,
                    landedLabel: "Open job",
                    undo: async () => {
                        await api.delete(`/api/attachments/${id}`);
                    },
                });
            } else {
                fd.append("category", "document");
                await api.postForm(`/api/sites/${target.siteId}/attachments`, fd);
                if (itemId)
                    await api
                        .patch(`/api/work-order-intake/items/${itemId}`, { status: "canceled" })
                        .catch(() => undefined);
                finishFiled(entry, {
                    message: `Not a recognized work order — filed to location documents on ${target.label}`,
                    landedHref: `/sites/${target.siteId}`,
                    landedLabel: "Open location",
                });
            }
        } catch (e) {
            patch(entry.key, { state: "error", message: "Upload failed" });
            captureClientError(e, { source: "UniversalDropLayer.autoLandUnknownPdf", fileName: entry.name });
        }
    }

    async function runAddressCheck(entry: DropEntry, itemId: string, force: boolean): Promise<void> {
        const target = entry.target;
        if (target.kind === "none") return;
        patch(entry.key, { state: "checking", busy: true, message: "Checking address…" });
        try {
            const body = target.kind === "job" ? { jobId: target.jobId, force } : { siteId: target.siteId, force };
            const res = await api.post<{
                linked: boolean;
                match: boolean;
                woAddress?: string;
                pageAddress?: string;
                woAddressMissing?: boolean;
            }>(`/api/work-order-intake/items/${itemId}/page-drop-link`, body);
            if (res.linked) {
                finishFiled(entry, {
                    message: res.match
                        ? `Work order linked to ${target.label} — address matched`
                        : `Work order linked to ${target.label} (office override)`,
                    landedHref: target.kind === "job" ? `/jobs/${target.jobId}` : `/sites/${target.siteId}`,
                    landedLabel: target.kind === "job" ? "Open job" : "Open location",
                    undo: async () => {
                        await api.patch(`/api/work-order-intake/items/${itemId}`, { status: "canceled" });
                    },
                });
                return;
            }
            // No service address on the WO while you're sitting on a job/location
            // page → treat it as "the system can't tell what this is" and land it
            // right here (Corbin's app law). A REAL address that simply mismatches
            // is a different story — never silently file under the wrong customer.
            if (res.woAddressMissing) {
                await autoLandUnknownPdf(entry, itemId);
                return;
            }
            patch(entry.key, {
                state: "mismatch",
                busy: false,
                woAddress: res.woAddress,
                pageAddress: res.pageAddress,
                message: "Address on the work order doesn't match this page",
            });
        } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
                const b = e.body as { message?: string } | null;
                patch(entry.key, {
                    state: "error",
                    busy: false,
                    message: b?.message ?? "Couldn't link the work order",
                });
                return;
            }
            patch(entry.key, { state: "error", busy: false, message: "Address check failed" });
            captureClientError(e, { source: "UniversalDropLayer.runAddressCheck", itemId });
        }
    }

    async function uploadWorkOrder(entry: DropEntry): Promise<void> {
        const target = entry.target;
        const fd = new FormData();
        fd.append("file", entry.file);
        // DROP-ON-A-JOB (Issue #289): tell the server which job we're standing on
        // so a test report attaches HERE instead of blind-routing by serial.
        if (target.kind === "job") fd.append("targetJobId", target.jobId);
        let itemId: string;
        try {
            const res = await api.postForm<{
                itemId: string;
                extractionAvailable?: boolean;
                diverted?: string;
                routed?: string;
                scoped?: string;
                jobId?: string;
                match?: { label?: string } | null;
                confirm?: {
                    assetId: string;
                    deviceLabel: string;
                    deviceSerial: string | null;
                    reportSerial: string | null;
                } | null;
                candidates?: JobDevice[] | null;
                multipage?: boolean;
                reportCount?: number;
                counts?: { filed: number; duplicate: number; review: number };
                summary?: string;
                workOrderCount?: number;
                created?: number;
                duplicate?: number;
            }>("/api/work-order-intake/upload", fd);
            // MULTI-REPORT PDF (#220): a fat PDF of many test reports was split
            // per page and each filed to its device — dropped right here on any
            // page. Show the tally and send review items to the City Filing Queue.
            if (res.diverted === "test_report" && res.multipage) {
                const c = res.counts ?? { filed: 0, duplicate: 0, review: 0 };
                finishFiled(entry, {
                    message: res.summary ?? "Multi-report PDF filed",
                    landedHref: c.review > 0 ? "/submissions" : "/my-uploads",
                    landedLabel: c.review > 0 ? "Review held reports" : "Find in My Uploads",
                });
                return;
            }
            // MULTI-WORK-ORDER PDF (#457): a fat vendor PDF that concatenated many
            // work orders (a Cintas bulk export is one order per few pages) was
            // split into per-order intake items. Show the tally and send the
            // office to the Intake Desk to review them.
            if (res.diverted === "work_order" && res.multipage) {
                finishFiled(entry, {
                    message: res.summary ?? `${res.workOrderCount ?? "Multiple"} work orders filed to the Intake Desk`,
                    landedHref: "/jobs/work-orders",
                    landedLabel: "Open Intake Desk",
                });
                return;
            }
            // DROP-ON-A-JOB: routed against the open job. filed = attached here;
            // confirm = serial mismatch on a single-device job (rip cord);
            // pick = the job has several devices, office chooses.
            if (res.diverted === "test_report" && res.scoped === "job" && target.kind === "job") {
                if (res.routed === "confirm" && res.confirm) {
                    patch(entry.key, {
                        state: "confirm",
                        busy: false,
                        confirmData: res.confirm,
                        message: "Serial doesn't match this job's device",
                    });
                    return;
                }
                if (res.routed === "pick" && res.candidates?.length) {
                    patch(entry.key, {
                        state: "pick",
                        busy: false,
                        pickDevices: res.candidates,
                        message: "Which device is this report for?",
                    });
                    return;
                }
                // filed
                finishFiled(entry, {
                    message: res.match?.label ?? `Test report filed to ${target.label}`,
                    landedHref: `/jobs/${target.jobId}`,
                    landedLabel: "Open job",
                });
                return;
            }
            // TEST REPORTS ARE NOT WORK ORDERS — the server classified this PDF
            // as a filed test report and routed it through the filing pipeline.
            if (res.diverted === "test_report") {
                const matched = res.routed === "filed";
                finishFiled(entry, {
                    message: matched
                        ? "Test report — matched and filed against its tested job"
                        : "Test report — needs one answer on the City Filing Queue",
                    landedHref: matched ? "/my-uploads" : "/submissions",
                    landedLabel: matched ? "Find in My Uploads" : "Open City Filing",
                });
                return;
            }
            itemId = res.itemId;
            patch(entry.key, { itemId, state: "extracting", message: "Reading the work order…" });
            if (res.extractionAvailable === false) {
                // Extraction is off. On a job/location page, land it here rather
                // than stranding it in intake; otherwise leave it for review.
                if (target.kind === "none") {
                    finishFiled(entry, {
                        message: "Filed to the Intake Desk for review",
                        landedHref: "/jobs/work-orders",
                        landedLabel: "Open Intake Desk",
                        undo: async () => {
                            await api.patch(`/api/work-order-intake/items/${itemId}`, { status: "canceled" });
                        },
                    });
                } else {
                    await autoLandUnknownPdf(entry, itemId);
                }
                return;
            }
        } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
                patch(entry.key, {
                    state: "error",
                    message: "Already uploaded — find it on the Intake Desk",
                    landedHref: "/jobs/work-orders",
                    landedLabel: "Open Intake Desk",
                });
                return;
            }
            patch(entry.key, { state: "error", message: "Upload failed" });
            captureClientError(e, { source: "UniversalDropLayer.uploadWorkOrder", fileName: entry.name });
            return;
        }

        const item = await pollItem(itemId);
        if (!item) {
            finishFiled(entry, {
                message: "Still reading it — it will land on the Intake Desk when done",
                landedHref: "/jobs/work-orders",
                landedLabel: "Open Intake Desk",
            });
            return;
        }
        if (item.status === "failed") {
            // Couldn't read it as a work order. On a job/location page that's the
            // "unknown → land here" case; off-context, send it to review.
            if (target.kind === "none") {
                patch(entry.key, {
                    state: "error",
                    message: "Couldn't read it — retry from the Intake Desk",
                    landedHref: "/jobs/work-orders",
                    landedLabel: "Open Intake Desk",
                });
                invalidate(target);
            } else {
                await autoLandUnknownPdf(entry, itemId);
            }
            return;
        }
        if (item.status === "duplicate") {
            patch(entry.key, {
                state: "error",
                message: "Duplicate work order — already in intake",
                landedHref: "/jobs/work-orders",
                landedLabel: "Open Intake Desk",
            });
            return;
        }
        if (target.kind === "none") {
            finishFiled(entry, {
                message: "Work order read — waiting on the Intake Desk",
                landedHref: "/jobs/work-orders",
                landedLabel: "Open Intake Desk",
                undo: async () => {
                    await api.patch(`/api/work-order-intake/items/${itemId}`, { status: "canceled" });
                },
            });
            return;
        }
        await runAddressCheck(entry, itemId, false);
    }

    // ── Drop-on-a-job rip cord (Issue #289): force-attach a test report to a
    // chosen device on the job the office is standing on (confirm / pick). ──
    async function forceAttachToJob(entry: DropEntry, assetId: string): Promise<void> {
        const target = entry.target;
        if (target.kind !== "job") return;
        patch(entry.key, { state: "uploading", busy: true, message: "Attaching to this job…" });
        const fd = new FormData();
        fd.append("file", entry.file);
        fd.append("targetJobId", target.jobId);
        fd.append("forceAssetId", assetId);
        try {
            const res = await api.postForm<{ routed?: string; match?: { label?: string } | null }>(
                "/api/work-order-intake/upload",
                fd,
            );
            finishFiled(entry, {
                message: res.match?.label ?? `Test report filed to ${target.label}`,
                landedHref: `/jobs/${target.jobId}`,
                landedLabel: "Open job",
            });
        } catch (e) {
            patch(entry.key, { state: "error", busy: false, message: "Couldn't attach it to this job" });
            captureClientError(e, { source: "UniversalDropLayer.forceAttachToJob", fileName: entry.name });
        }
    }

    // ── Drop / pick handling ──────────────────────────────────────────────

    const handleFiles = useCallback(
        (files: File[]) => {
            const target = resolveTarget();
            const next: DropEntry[] = files.map((file) => {
                keyCounter.current += 1;
                const route: DropEntry["route"] = isPdfFile(file)
                    ? "work_order"
                    : isImageFile(file)
                      ? "photo"
                      : "document";
                return {
                    key: `drop-${keyCounter.current}`,
                    file,
                    name: file.name,
                    target,
                    route,
                    state: "uploading",
                    message: route === "work_order" ? "Sorting…" : "Uploading…",
                    thumbUrl: isImageFile(file) ? URL.createObjectURL(file) : null,
                    chip: "doc",
                    attachmentId: null,
                    itemId: null,
                    landedHref: null,
                    landedLabel: null,
                    undo: null,
                };
            });
            setEntries((prev) => [...prev, ...next]);
            for (const entry of next) {
                if (entry.route === "work_order") {
                    void uploadWorkOrder(entry);
                } else if (entry.target.kind === "none") {
                    // A photo/other file with nowhere to land — don't guess a
                    // customer. Tell the user where to drop it.
                    patch(entry.key, {
                        state: "error",
                        message: "Open a job or location to file this — or drop a report/work order anywhere",
                    });
                } else {
                    void uploadPhotoOrDoc(entry);
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    function onPick(e: ChangeEvent<HTMLInputElement>): void {
        const files = Array.from(e.target.files ?? []);
        if (files.length > 0) void normalizePdfFiles(files).then(handleFiles);
        e.target.value = ""; // allow re-picking the same file
    }

    // ── Tray row actions ──────────────────────────────────────────────────

    async function setChip(entry: DropEntry, chip: PhotoChip): Promise<void> {
        patch(entry.key, { chip });
        if (entry.attachmentId && entry.target.kind === "job") {
            try {
                await api.patch(`/api/attachments/${entry.attachmentId}`, { photoTag: chip });
                patch(entry.key, { message: `Filed as photo (${chip}) on ${entry.target.label}` });
            } catch {
                toast.error("Couldn't update the photo category");
            }
        }
    }

    function dismiss(entry: DropEntry): void {
        if (entry.thumbUrl) URL.revokeObjectURL(entry.thumbUrl);
        setEntries((prev) => prev.filter((e) => e.key !== entry.key));
    }

    // Revoke any outstanding object URLs on unmount.
    useEffect(() => {
        return () => {
            entries.forEach((e) => e.thumbUrl && URL.revokeObjectURL(e.thumbUrl));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const overlayLabel =
        previewTarget.kind === "none" ? "Drop it — I'll sort it" : `Drop to file under ${previewTarget.label}`;

    // ── Render ────────────────────────────────────────────────────────────

    return (
        <>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={onPick} />

            {/* Full-screen "Drop it — I'll sort it" overlay while dragging.
                Suppressed inside the closeout job workspace so the page's own
                scoped drop zones (Send-off "Additional files", the Documents
                station) can receive the drop instead of this overlay. */}
            {dragActive && !suppressOverlay && (
                <div
                    className={cn(
                        "fixed inset-0 z-[70] flex items-center justify-center transition-colors",
                        overlayHover ? "bg-primary/25 backdrop-blur-[2px]" : "bg-primary/15",
                    )}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setOverlayHover(true);
                    }}
                    onDragLeave={() => setOverlayHover(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setOverlayHover(false);
                        const files = Array.from(e.dataTransfer?.files ?? []);
                        if (files.length > 0) void normalizePdfFiles(files).then(handleFiles);
                    }}
                >
                    <div className="pointer-events-none flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-primary bg-card px-10 py-8 shadow-xl">
                        <UploadCloud className="h-10 w-10 text-primary" />
                        <p className="text-lg font-semibold text-card-foreground">{overlayLabel}</p>
                        <p className="max-w-sm text-center text-sm text-muted-foreground">
                            Test reports file themselves · work orders check the address before linking · anything else
                            {previewTarget.kind === "none"
                                ? " waits for review on the Intake Desk"
                                : " lands on this page"}
                        </p>
                    </div>
                </div>
            )}

            {/* Permanent subtle drop pill — the only always-on drop chrome.
                Click to pick a file (tablet / keyboard path). Hidden while the
                full-screen overlay is up, and while a local drop owner (the email
                composer) is open — there, a picked file belongs to that surface,
                not the universal router. (The closeout case keeps its pill: only
                the overlay is suppressed there, per the bible.) */}
            {!dragActive && !localDropOwner && (
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title="Drop a report or work order anywhere — or click to pick one"
                    // eslint-disable-next-line no-restricted-syntax -- D1: fixed overlay floating above the page; the blur is load-bearing
                    className="fixed bottom-4 left-4 z-[55] hidden items-center gap-1.5 rounded-full border border-border/70 bg-card/85 px-3 py-1.5 text-xs font-medium text-foreground/70 shadow-sm backdrop-blur-sm transition-colors hover:border-primary/50 hover:text-foreground sm:flex"
                >
                    <UploadCloud className="h-3.5 w-3.5 text-primary" />
                    Drop a file anywhere
                </button>
            )}

            {/* Receipt tray — one row per dropped file, newest last. */}
            {entries.length > 0 && (
                <div className="fixed bottom-4 right-4 z-[60] w-[26rem] max-w-[calc(100vw-2rem)] space-y-2 rounded-xl border border-border bg-card p-3 text-card-foreground shadow-xl">
                    <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold uppercase tracking-wider text-foreground/70">
                            Dropped files
                        </p>
                        <div className="flex items-center gap-2">
                            <Link to="/my-uploads" className="text-[11px] font-medium text-primary hover:underline">
                                My Uploads
                            </Link>
                            <button
                                type="button"
                                className="rounded p-0.5 text-foreground/60 hover:bg-muted hover:text-foreground"
                                onClick={() => {
                                    entries.forEach((e) => e.thumbUrl && URL.revokeObjectURL(e.thumbUrl));
                                    setEntries([]);
                                }}
                                aria-label="Clear receipts"
                            >
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    </div>
                    <ul className="max-h-[50vh] space-y-2 overflow-y-auto">
                        {entries.map((entry) => (
                            <li key={entry.key} className="rounded-lg border border-border/70 p-2">
                                <div className="flex items-start gap-2">
                                    {entry.thumbUrl ? (
                                        <img
                                            src={entry.thumbUrl}
                                            alt=""
                                            className="h-10 w-10 shrink-0 rounded object-cover"
                                        />
                                    ) : (
                                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
                                            {entry.route === "photo" ? (
                                                <ImageIcon className="h-4 w-4 text-foreground/60" />
                                            ) : (
                                                <FileText className="h-4 w-4 text-foreground/60" />
                                            )}
                                        </span>
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-xs font-medium" title={entry.name}>
                                            {entry.name}
                                        </p>
                                        <p
                                            className={cn(
                                                "mt-0.5 flex items-center gap-1 text-xs",
                                                entry.state === "filed"
                                                    ? "text-emerald-600"
                                                    : entry.state === "error"
                                                      ? "text-destructive"
                                                      : entry.state === "mismatch" ||
                                                          entry.state === "confirm" ||
                                                          entry.state === "pick"
                                                        ? "text-amber-600"
                                                        : entry.state === "undone"
                                                          ? "text-foreground/60"
                                                          : "text-foreground/60",
                                            )}
                                        >
                                            {(entry.state === "uploading" ||
                                                entry.state === "extracting" ||
                                                entry.state === "checking") && (
                                                <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
                                            )}
                                            {entry.state === "filed" && <CheckCircle2 className="h-3 w-3 shrink-0" />}
                                            {(entry.state === "mismatch" ||
                                                entry.state === "confirm" ||
                                                entry.state === "pick") && (
                                                <AlertTriangle className="h-3 w-3 shrink-0" />
                                            )}
                                            {entry.state === "error" && <XCircle className="h-3 w-3 shrink-0" />}
                                            {entry.state === "undone" && <Undo2 className="h-3 w-3 shrink-0" />}
                                            <span className="min-w-0">{entry.message}</span>
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className="rounded p-0.5 text-foreground/40 hover:bg-muted hover:text-foreground"
                                        onClick={() => dismiss(entry)}
                                        aria-label="Dismiss"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>

                                {/* Filed row: Undo (when reversible) + open where it landed. */}
                                {entry.state === "filed" && (entry.undo || entry.landedHref) && (
                                    <div className="mt-1.5 flex items-center gap-2 pl-12">
                                        {entry.undo && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 gap-1 px-2 text-[11px]"
                                                disabled={entry.busy}
                                                onClick={() => void runUndo(entry)}
                                            >
                                                <Undo2 className="h-3 w-3" /> Undo
                                            </Button>
                                        )}
                                        {entry.landedHref && (
                                            <Link
                                                to={entry.landedHref}
                                                className="text-[11px] text-primary hover:underline"
                                            >
                                                {entry.landedLabel ?? "Open"}
                                            </Link>
                                        )}
                                    </div>
                                )}

                                {/* Photo category chips. */}
                                {entry.route === "photo" && entry.state === "filed" && (
                                    <div className="mt-1.5 flex items-center gap-1 pl-12">
                                        {PHOTO_CHIPS.map((chip) => (
                                            <button
                                                key={chip}
                                                type="button"
                                                onClick={() => void setChip(entry, chip)}
                                                className={cn(
                                                    "rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors",
                                                    entry.chip === chip
                                                        ? "border-primary bg-primary text-primary-foreground"
                                                        : "border-border text-muted-foreground hover:border-primary/50",
                                                )}
                                            >
                                                {chip}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* WO address mismatch — never auto-links under the wrong customer. */}
                                {entry.state === "mismatch" && (
                                    <div className="mt-2 space-y-1.5 pl-12">
                                        <p className="text-[11px] leading-snug text-foreground/70">
                                            Work order says <span className="font-medium">{entry.woAddress}</span> —
                                            this page is <span className="font-medium">{entry.pageAddress}</span>
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 px-2 text-[11px]"
                                                disabled={entry.busy}
                                                onClick={() => void autoLandUnknownPdf(entry, entry.itemId)}
                                            >
                                                File here anyway
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="h-6 gap-1 px-2 text-[11px]"
                                                disabled={entry.busy || !entry.itemId}
                                                onClick={() =>
                                                    entry.itemId && void runAddressCheck(entry, entry.itemId, true)
                                                }
                                            >
                                                <Link2 className="h-3 w-3" /> Link as work order
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Drop-on-a-job confirm rip cord (Issue #289) — the
                                    report's serial didn't match this job's device.
                                    Force-attach only on an explicit click. */}
                                {entry.state === "confirm" && entry.confirmData && (
                                    <div className="mt-2 space-y-1.5 pl-12">
                                        <p className="text-[11px] leading-snug text-foreground/70">
                                            {entry.confirmData.reportSerial ? (
                                                <>
                                                    Report reads{" "}
                                                    <span className="font-medium">
                                                        SN {entry.confirmData.reportSerial}
                                                    </span>{" "}
                                                    —
                                                </>
                                            ) : (
                                                <>The report&apos;s serial didn&apos;t match</>
                                            )}{" "}
                                            this job&apos;s device is{" "}
                                            <span className="font-medium">{entry.confirmData.deviceLabel}</span>.
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-6 px-2 text-[11px]"
                                                disabled={entry.busy}
                                                onClick={() =>
                                                    entry.confirmData &&
                                                    void forceAttachToJob(entry, entry.confirmData.assetId)
                                                }
                                            >
                                                Attach to this job anyway
                                            </Button>
                                            <button
                                                type="button"
                                                className="text-[11px] text-foreground/50 hover:text-foreground"
                                                disabled={entry.busy}
                                                onClick={() => dismiss(entry)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Drop-on-a-job pick card — the job has several
                                    devices; the office chooses which one. */}
                                {entry.state === "pick" && entry.pickDevices && (
                                    <div className="mt-2 space-y-1.5 pl-12">
                                        <p className="text-[11px] leading-snug text-foreground/70">
                                            Which device is this report for?
                                        </p>
                                        <div className="flex flex-col gap-1">
                                            {entry.pickDevices.map((d) => (
                                                <Button
                                                    key={d.assetId}
                                                    size="sm"
                                                    variant="outline"
                                                    className="h-auto justify-start px-2 py-1 text-left text-[11px]"
                                                    disabled={entry.busy}
                                                    onClick={() => void forceAttachToJob(entry, d.assetId)}
                                                >
                                                    {d.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Error / off-context escape hatches. */}
                                {entry.state === "error" && entry.landedHref && (
                                    <div className="mt-1.5 pl-12">
                                        <Link
                                            to={entry.landedHref}
                                            className="text-[11px] text-primary hover:underline"
                                        >
                                            {entry.landedLabel ?? "Open"}
                                        </Link>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </>
    );
}
